'use client';

/**
 * VendorActionsCard — contextual action stack (rail).
 *
 * Mode4-native parallel paint of the legacy Actions tab. Reads the
 * `vendor_actions` strategy and renders a stack of action buttons.
 * A product selection passes its own sid (vendor_actions decides what
 * to offer per entity type — PT_ gets `more_like_this`, CO_ doesn't);
 * other selections fall back to the parent vendor via the active tab.
 *
 * Most actions (create_rfq, view_catalog, send_message) are still
 * unwired UI stubs pending the action gateway. `more_like_this` is
 * wired: it fetches the product's query (catalog_more_like_this_query),
 * seeds mode3's composer, and switches the explorer there.
 *
 * @see klyde_edge/ui_config/composition/cards/vendors.py — content_key="vendor_actions"
 * @see klyde_edge/klyde_intelligence/vendors/handler.py — vendor_actions strategy
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { useMemo, useState } from 'react';
import { Loader2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api/client';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useTabStore } from '@/lib/stores/tab-store';
import { switchExplorerMode } from '@/lib/stores/tab-store';
import { useIntentStore } from '@/lib/stores/intent-store';
import { useCardExport } from '@/lib/design/card-export';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { RailCard } from '../rail-card';
import { ActionButton, type ActionItem } from '@/components/ui/action-button';

interface ActionsResponse {
  semantic_id?: string;
  actions?: ActionItem[];
}

interface MoreLikeThisQueryResponse {
  success: boolean;
  query_text?: string;
  image_base64?: string | null;
  error?: string | null;
}

const VENDOR_ACTIONS_STRATEGY = 'vendor_actions';
const MORE_LIKE_THIS_QUERY_STRATEGY = 'catalog_more_like_this_query';

export function VendorActionsCard() {
  const { entityType, entityId } = useViewportSelection();
  // Resolve the actions-list sid: a PRODUCT selection passes its own
  // sid directly (vendor_actions decides what to offer per entity type
  // — PT_ gets more_like_this, CO_ doesn't), everything else falls back
  // to the vendor (current selection, else the active tab) same as before.
  const activeTabId = useTabStore((s) => s.activeTabId);
  const tabs = useTabStore((s) => s.tabs);
  const activeTab = tabs.find((t) => t.id === activeTabId);
  const vendorFromTab =
    activeTab?.type === 'vendor' ? activeTab.entityId ?? null : null;
  const vendorSid =
    entityType === 'vendor' && entityId ? entityId : vendorFromTab;
  const actionsSemanticId =
    entityType === 'product' && entityId ? entityId : vendorSid;

  const enabled = !!actionsSemanticId;

  const { data, isLoading, error } = useStrategyData<ActionsResponse>(
    VENDOR_ACTIONS_STRATEGY,
    enabled ? { semantic_id: actionsSemanticId ?? '' } : {},
    enabled,
  );

  const seedSearch = useIntentStore((s) => s.seedSearch);
  const [moreLikeThisLoading, setMoreLikeThisLoading] = useState(false);

  const handleMoreLikeThis = async () => {
    if (entityType !== 'product' || !entityId || moreLikeThisLoading) return;
    setMoreLikeThisLoading(true);
    try {
      const res = await api.getStrategyData<MoreLikeThisQueryResponse>(
        MORE_LIKE_THIS_QUERY_STRATEGY,
        { semantic_id: entityId },
      );
      if (!res.success || !res.data?.success || !res.data.query_text) {
        toast.error(res.data?.error || res.error || 'Could not build a similar-products search');
        return;
      }
      switchExplorerMode('mode3');
      seedSearch({
        text: res.data.query_text,
        images: res.data.image_base64 ? [res.data.image_base64] : [],
      });
    } finally {
      setMoreLikeThisLoading(false);
    }
  };

  // Scene export — query-backed; the available actions for this vendor.
  useCardExport(useMemo(() => {
    const actions = data?.data?.actions ?? [];
    const status: 'loading' | 'loaded' | 'error' | 'idle' = !enabled
      ? 'idle' : isLoading ? 'loading' : error ? 'error' : 'loaded';
    return {
      label: 'Actions',
      content_key: 'vendor_actions',
      surface: 'rail' as const,
      status,
      query_key: enabled
        ? ['strategy-data', VENDOR_ACTIONS_STRATEGY, { semantic_id: actionsSemanticId ?? '' }]
        : null,
      text: actions.length ? actions.map((a) => a.label).join(', ') : 'Actions',
      json: { actions: actions.map((a) => ({ key: a.key, label: a.label, disabled: !!a.disabled })) },
    };
  }, [data, enabled, isLoading, error, actionsSemanticId]));

  if (!enabled) {
    return (
      <RailCard title="Actions" icon={Zap}>
        <div className={text.muted}>
          Select a vendor to see available actions
        </div>
      </RailCard>
    );
  }

  if (isLoading) {
    return (
      <RailCard title="Actions" icon={Zap}>
        <div className={cn(text.muted, 'flex items-center gap-2')}>
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading…
        </div>
      </RailCard>
    );
  }

  if (error) {
    return (
      <RailCard title="Actions" icon={Zap}>
        <div className={cn(text.muted, 'text-destructive')}>
          Failed: {(error as Error).message}
        </div>
      </RailCard>
    );
  }

  const actions = data?.data?.actions ?? [];
  if (actions.length === 0) {
    return (
      <RailCard title="Actions" icon={Zap}>
        <div className={text.muted}>
          No actions available for this selection.
        </div>
      </RailCard>
    );
  }

  return (
    <RailCard title="Actions" icon={Zap}>
      <ul className="space-y-1.5">
        {actions.map((a) => (
          <li key={a.key}>
            <ActionButton
              action={a}
              className="w-full justify-start"
              onClick={a.key === 'more_like_this' ? handleMoreLikeThis : undefined}
              loading={a.key === 'more_like_this' ? moreLikeThisLoading : undefined}
            />
          </li>
        ))}
      </ul>
    </RailCard>
  );
}
